import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import styles from './GlobalSearch.module.scss';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { ROUTES } from '@/admin/config/routes';

interface SearchResult {
  id: string;
  title: string;
  subtitle: string;
  type: 'deal' | 'district' | 'school' | 'user';
  path: string;
  status?: 'active' | 'inactive';
  icon: string;
}

interface GlobalSearchProps {
  placeholder?: string;
  className?: string;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ placeholder, className }) => {
  const { t } = useAdminTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [results, setResults] = useState<SearchResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Dummy data - replace with actual API call
  const mockData: SearchResult[] = [
    {
      id: '1',
      title: 'Lochee',
      subtitle: 'Customer Pilot',
      type: 'deal',
      path: ROUTES.ACCOUNT_DETAILS('1'),
      status: 'active',
      icon: 'deals',
    },
    {
      id: '2',
      title: 'Hilltown',
      subtitle: 'Product Demo',
      type: 'deal',
      path: ROUTES.ACCOUNT_DETAILS('2'),
      status: 'inactive',
      icon: 'deals',
    },
    {
      id: '3',
      title: 'West End',
      subtitle: 'Test District',
      type: 'district',
      path: '/districts/3',
      icon: 'districts',
    },
    {
      id: '4',
      title: 'Stobswell',
      subtitle: 'Customer Pilot',
      type: 'deal',
      path: ROUTES.ACCOUNT_DETAILS('4'),
      status: 'active',
      icon: 'deals',
    },
    {
      id: '5',
      title: 'Santa Clara School',
      subtitle: 'São Paulo District',
      type: 'school',
      path: '/schools/5',
      icon: 'schools',
    },
    {
      id: '6',
      title: 'John Smith',
      subtitle: 'Admin User',
      type: 'user',
      path: '/user-management/6',
      icon: 'userManagement',
    },
    {
      id: '7',
      title: 'City Centre',
      subtitle: 'Product Demo',
      type: 'deal',
      path: ROUTES.ACCOUNT_DETAILS('7'),
      status: 'inactive',
      icon: 'deals',
    },
    {
      id: '8',
      title: 'Downtown District',
      subtitle: 'Active District',
      type: 'district',
      path: '/districts/8',
      icon: 'districts',
    },
  ];

  const searchData = (searchQuery: string): SearchResult[] => {
    if (!searchQuery.trim()) return [];

    const filtered = mockData.filter(
      item =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.subtitle.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return filtered.slice(0, 8); // Limit to 8 results
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    if (value.trim()) {
      const searchResults = searchData(value);
      setResults(searchResults);
      setIsOpen(true);
      setSelectedIndex(-1);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleResultClick(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
    setQuery('');
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  const handleFocus = () => {
    if (query.trim() && results.length > 0) {
      setIsOpen(true);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Delay closing to allow clicking on results
    setTimeout(() => {
      if (!resultsRef.current?.contains(e.relatedTarget as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    }, 150);
  };

  const getTypeColor = (type: string) => {
    const colors = {
      deal: 'blue',
      district: 'purple',
      school: 'green',
      user: 'orange',
    };
    return colors[type as keyof typeof colors] || 'gray';
  };

  const getStatusColor = (status?: string) => {
    return status === 'active' ? 'green' : 'gray';
  };

  return (
    <div className={clsx(styles.globalSearch, className)}>
      <div className={styles.searchInput}>
        <AppIcon icon='Search' className={styles.searchIcon} />
        <input
          ref={inputRef}
          type='text'
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder || t('common.search') || ''}
          className={styles.input}
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setResults([]);
              setIsOpen(false);
            }}
            className={styles.clearButton}
          >
            <AppIcon icon='x' />
          </button>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div
          ref={resultsRef}
          className={styles.searchResults}
          onMouseDown={e => e.preventDefault()}
        >
          <div className={styles.resultsHeader}>
            <span className={styles.resultsCount}>
              {results.length} {results.length === 1 ? 'result' : 'results'}
            </span>
          </div>

          <div className={styles.resultsList}>
            {results.map((result, index) => (
              <div
                key={result.id}
                className={clsx(styles.resultItem, {
                  [styles.selected]: index === selectedIndex,
                })}
                onClick={() => handleResultClick(result)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className={styles.resultIcon}>
                  <AppIcon icon={result.icon as any} />
                </div>
                <div className={styles.resultContent}>
                  <div className={styles.resultTitle}>{result.title}</div>
                  <div className={styles.resultSubtitle}>{result.subtitle}</div>
                </div>
                <div className={styles.resultMeta}>
                  <span className={clsx(styles.typeTag, styles[getTypeColor(result.type)])}>
                    {result.type}
                  </span>
                  {result.status && (
                    <span className={clsx(styles.statusTag, styles[getStatusColor(result.status)])}>
                      {result.status}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.resultsFooter}>
            <span className={styles.hint}>
              Press <kbd>↑</kbd>
              <kbd>↓</kbd> to navigate, <kbd>Enter</kbd> to select, <kbd>Esc</kbd> to close
            </span>
          </div>
        </div>
      )}

      {isOpen && query && results.length === 0 && (
        <div className={styles.noResults}>
          <div className={styles.noResultsContent}>
            <AppIcon icon='Search' className={styles.noResultsIcon} />
            <span>{`No results found for "${query}"`}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
